import { TranslateService } from '@ngx-translate/core';
import { ActionsObservable, ofType, StateObservable } from 'redux-observable';
import { from, of } from 'rxjs';
import {
    catchError,
    concatMap,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';

import { CmpsDatePipe } from '../../common/date.pipe';
import { CmpsDecimalPipe, CmpsPercentPipe } from '../../common/number.pipe';
import { Exporter } from '../../insight/experimental.grid-export';
import { Dependencies } from '../../store';
import { pluck } from '../../store.decorator';
import { PayloadAction } from '../../store.model';
import { UserService } from '../../user/user.service';
import { ScorecardExportService } from '../core/export.service';
import { GridType } from '../core/template-definition.model';
import { listDataObservable } from '../list-data';
import { CmpsNumberWithSign } from '../numberWithSign.pipe';
import { retrieveBoxesObservable } from '../retrieve-boxes';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { TranslationKeyMap } from '../scorecard.i18n';
import { PeriodType } from '../scorecard.model';
import { CompensationDescriptionPipe } from '../widgets/compensation-description.pipe';
import { GridEpicBridge } from '../widgets/grid/grid.epic.bridge';

import { exportMultipleWidget } from './grid-export';
import { GridTab } from './grid-tab.model';
import {
    ActionType,
    CreateTab,
    createTabResult,
    Export,
    exportScorecardResult,
    ListData,
    listDataResult,
    Reload,
    reloadResult,
    WidgetsExport,
    widgetsExportResult,
} from './scorecard.action';
import { ScorecardState, ScorecardTab } from './scorecard.state';

interface ChainGroup<T> {
    payload: T;

    tab?: ScorecardTab;
    periodTypes?: PeriodType[];
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const createTab = (
    action: ActionsObservable<PayloadAction<ActionType, CreateTab>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCORECARD__CREATE_TAB),
        mergeMap((act) => {
            const service = deps.injector.get<UserService>(UserService);

            let obs = of(act.payload.user);
            if (act.payload.userId != null) {
                obs = obs.pipe(
                    concatMap(() => service.retrieve(act.payload.userId)),
                );
            }

            return obs.pipe(
                map((ret) =>
                    createTabResult(
                        { ...act.payload, user: ret },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        createTabResult({ ...act.payload }, act.storePath),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const reload = (
    action: ActionsObservable<PayloadAction<ActionType, Reload>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCORECARD__RELOAD),
        mergeMap((act) => {
            const rootState = pluck<ScorecardState>(state.value, act.storePath);
            const grp: ChainGroup<Reload> = { payload: act.payload };

            return of(null).pipe(
                tap(() => {
                    grp.tab = rootState.tabs.get(grp.payload.tabId);
                    if (!grp.tab) {
                        throw new Error('no tab');
                    }
                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    grp.periodTypes =
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        grp.payload.periodTypes &&
                        grp.payload.periodTypes.length
                            ? grp.payload.periodTypes
                            : [null];
                }),
                concatMap(() => {
                    return retrieveBoxesObservable(
                        deps,
                        grp.periodTypes,
                        grp.tab.userId,
                        grp.payload.scorecardId,
                        grp.payload.groupId,
                        grp.payload.endDate,
                        grp.tab.useLiveData,
                    );
                }),
                map((ret) =>
                    reloadResult(
                        { ...grp.payload, scorecardBoxes: ret },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(reloadResult({ ...act.payload }, act.storePath));
                }),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.SCORECARD__RELOAD_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.payload.tabId === act.payload.tabId,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const listData = (
    action: ActionsObservable<PayloadAction<ActionType, ListData>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCORECARD__LIST_DATA),
        mergeMap((act) => {
            return of(null).pipe(
                concatMap(() => {
                    return listDataObservable(
                        deps,
                        act.payload.scorecardIds,
                        act.payload.useLiveData,
                    );
                }),
                map((ret) =>
                    listDataResult(
                        { ...act.payload, scorecardData: ret },
                        act.storePath,
                    ),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        listDataResult({ ...act.payload }, act.storePath),
                    );
                }),
                takeUntil(
                    action.pipe(
                        ofType(ActionType.SCORECARD__LIST_DATA_CANCEL),
                        filter(
                            (cancelAct) =>
                                cancelAct.payload.tabId === act.payload.tabId,
                        ),
                    ),
                ),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const exportScorecard = (
    action: ActionsObservable<PayloadAction<ActionType, Export>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCORECARD__EXPORT),
        switchMap((act) => {
            const rootState = pluck<ScorecardState>(state.value, act.storePath);
            const service = deps.injector.get<ScorecardExportService>(
                ScorecardExportService,
            );
            const datePipe = deps.injector.get<CmpsDatePipe>(CmpsDatePipe);
            const grp: ChainGroup<Export> = { payload: act.payload };

            return of(null).pipe(
                concatMap(() => {
                    const tab = rootState.selectedTab;
                    const card = tab.selectedScorecard;
                    const start = datePipe.transform(
                        card.header.startDate,
                        'MM-dd-y',
                    );
                    const end = datePipe.transform(
                        card.header.endDate,
                        'MM-dd-y',
                    );

                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    const filename = `scorecard_${tab.userId}_${card.header.scorecardId}_${start}_${end}.pdf`;
                    return service.run(filename);
                }),
                map(() =>
                    exportScorecardResult({ ...grp.payload }, act.storePath),
                ),
                catchError((err) => {
                    console.log(err);
                    return of(
                        exportScorecardResult(
                            { ...act.payload },
                            act.storePath,
                        ),
                    );
                }),
            );
        }),
    );

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const widgetsExport = (
    action: ActionsObservable<PayloadAction<ActionType, WidgetsExport>>,
    state: StateObservable<any>,
    deps: Dependencies,
) =>
    action.pipe(
        ofType(ActionType.SCORECARD__WIDGETS_EXPORT),
        mergeMap((act) => {
            const bridge = deps.injector.get<GridEpicBridge>(GridEpicBridge);
            const decimalPipe =
                deps.injector.get<CmpsDecimalPipe>(CmpsDecimalPipe);
            const percentPipe =
                deps.injector.get<CmpsPercentPipe>(CmpsPercentPipe);
            const datePipe = deps.injector.get<CmpsDatePipe>(CmpsDatePipe);
            const numberWithSignPipe =
                deps.injector.get<CmpsNumberWithSign>(CmpsNumberWithSign);
            const compDescPipe = deps.injector.get<CompensationDescriptionPipe>(
                CompensationDescriptionPipe,
            );

            const translateService =
                deps.injector.get<TranslateService>(TranslateService);

            const exp = new Exporter();
            const rootState = pluck<ScorecardState>(state.value, act.storePath);
            const user = rootState.selectedTab.user;
            const periodEndDate = datePipe.transform(
                rootState.selectedTab.selectedScorecard.header.endDate,
                'yMMdd',
            );
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            let filename = `${user.firstName}_${user.lastName}_Scorecard_${periodEndDate}.xlsx`;

            if (
                rootState.selectedTab.selectedGridTab ===
                GridTab.DIRECT_REPORT_VIEW
            ) {
                switch (
                    rootState.selectedTab.selectedDirectReportDropdownItem
                ) {
                    case GridType.Rank_View:
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        filename = `${user.firstName}_${user.lastName}_RankView_${periodEndDate}.xlsx`;
                        break;
                    case GridType.Contribution_View:
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        filename = `${user.firstName}_${user.lastName}_ContributionView_${periodEndDate}.xlsx`;
                        break;
                    case GridType.Direct_Reports_View:
                        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                        filename = `${user.firstName}_${user.lastName}_DirectReportView_${periodEndDate}.xlsx`;
                        break;
                }
            }

            return of(null).pipe(
                mergeMap(() => from(exp.createEmptyWorkbook())),
                map(() => {
                    exportMultipleWidget(
                        act,
                        rootState,
                        exp,
                        bridge,
                        translateService,
                        decimalPipe,
                        percentPipe,
                        datePipe,
                        numberWithSignPipe,
                        compDescPipe,
                    );
                }),
                // eslint-disable-next-line @typescript-eslint/promise-function-async
                map(() => exp.serve(filename).then(() => exp.destroy())),
                map(() => widgetsExportResult({}, act.storePath)),
                catchError((e) => {
                    console.log('error: ', e);
                    exp.destroy();
                    return of(widgetsExportResult({}, act.storePath));
                }),
            );
        }),
    );

export default [createTab, reload, listData, exportScorecard, widgetsExport];
